import {
  APP_INITIALIZER,
  CUSTOM_ELEMENTS_SCHEMA,
  ErrorHandler,
  Injectable,
  NgModule,
} from "@angular/core";
import { BrowserModule } from "@angular/platform-browser";
import { Router, RouteReuseStrategy } from "@angular/router";
import {
  HTTP_INTERCEPTORS,
  HttpClientModule
} from "@angular/common/http";
import { ReactiveFormsModule } from "@angular/forms";
import { ModalController } from "@ionic/angular";
import { AngularFireModule } from "@angular/fire/compat";
import { AngularFireStorageModule } from "@angular/fire/compat/storage";
import { IonicModule, IonicRouteStrategy } from "@ionic/angular";
import * as Sentry from "@sentry/angular-ivy";
import { InMemoryWebApiModule } from "angular-in-memory-web-api";

import { AppRoutingModule } from "./app-routing.module";
import { AppComponent } from "./app.component";
import { FakeDbService } from "./fake-db/fake-db.service";
import { ServiceWorkerModule } from "@angular/service-worker";
import { environment } from "../environments/environment";
import { AuthGuard } from "./shared/guard/auth.guard";
import { IntercomModule } from "@supy-io/ngx-intercom";
import { FileUploadComponentModule } from "./shared/components/file-upload/file-upload.module";
import { UtilService } from "./shared/services/util.service";
import { AngularFireAuthModule } from "@angular/fire/compat/auth";
import { VERSION } from "src/environments/version";
import { PreferencePageModule } from "./main/preference/preference.module";
import { ConfigurationService } from "./shared/services/configuration.service";
import { AppInterceptor } from "./shared/interceptor/app.interceptor";
import { LaunchDarklyService } from "./shared/services/launchdarkly.service";
import { LaunchDarklyModule } from "./shared/launch-darkly.module";
import { Capacitor } from "@capacitor/core";

// Detect Cypress test runner to avoid noisy dialogs and SW caching during e2e
const isCypress = typeof window !== 'undefined' && (window as any)?.Cypress;

export function initLDAnon(ld: LaunchDarklyService) {
  const anonContext = {
    kind: "multi",
    user: { key: "anon", anonymous: true }
  } as const;
  return () => ld.initialize(anonContext);
}

if (!isCypress && (environment.production || environment.stage || environment.test)) {
  Sentry.init({
    dsn: environment["sentryDsn"],
    environment: environment.env,
    debug: false,
    release: `taskezy@${VERSION.version}.${VERSION.abbreviatedSha}`,
    integrations: [
      new Sentry.BrowserTracing({
        tracingOrigins: ["*"],
        routingInstrumentation: Sentry.routingInstrumentation,
      })
      // new Sentry.Integrations.TryCatch({
      //   XMLHttpRequest: false,
      // }),
      // new Integrations.BrowserTracing({
      // tracingOrigins: ["*"],
      // routingInstrumentation: Sentry.routingInstrumentation,
      // }),
      // new ExtraErrorDataIntegration({ depth: 8 }),
      // new CaptureConsoleIntegration({
      //   levels: ["warn", "error", "debug", "assert"],
      // }),
      // new DedupeIntegration(),
    ],
    tracesSampleRate: 0.3,
    attachStacktrace: true,
    normalizeDepth: 8,
  });
}

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    IonicModule.forRoot({
      backButtonIcon: "arrow-back",
      backButtonText: "",
      animated: true,
    }),
    AngularFireModule.initializeApp(environment.firebase),
    AngularFireStorageModule,
    AngularFireAuthModule,
    AppRoutingModule,
    HttpClientModule,
    ReactiveFormsModule,
    InMemoryWebApiModule.forRoot(FakeDbService, {
      delay: 0,
      passThruUnknownUrl: true,
      post204: false,
      put204: false,
    }),
    ServiceWorkerModule.register("ngsw-worker.js", {
      enabled: (!isCypress && !environment.dev && !environment.e2e) && !Capacitor.isNativePlatform(),
      registrationStrategy: 'registerWhenStable:30000'
    }),
    IntercomModule.forRoot({
      appId: environment.intercomAPIKey,
      updateOnRouterChange: true,
    }),
    FileUploadComponentModule,
    PreferencePageModule,
    LaunchDarklyModule,
  ],
  providers: [
    FakeDbService,
    ModalController,
    AuthGuard,
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        // Never show the Sentry feedback dialog during Cypress runs
        showDialog: !environment.production && !isCypress,
        logErrors: true,
      }),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => { },
      deps: [Sentry.TraceService],
      multi: true,
    },
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    { provide: HTTP_INTERCEPTORS, useClass: AppInterceptor, multi: true },
    UtilService,
    {
      provide: ConfigurationService,
      useFactory: () => {
        const configService = new ConfigurationService();

        let env = environment.env;

        if (env != 'e2e-resource') {
          try {
            const localStorageEnv = localStorage.getItem("__env_ovr");
            if (localStorageEnv) {
              env = localStorageEnv;
            }
          } catch (error) {
            console.error('Failed to access localStorage', error);
          }
        }

        configService.initialize(env);
        return configService;
      }
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initLDAnon,
      deps: [LaunchDarklyService],
      multi: true
    }
  ],
  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class AppModule { }
